// Battleship board and fleet definitions (suunnitelma.md sections 9–10).

/** 10 × 10 board. Columns A–J (x = 0–9), rows 1–10 (y = 0–9, bottom to top). A1 is bottom-left. */
export const BOARD_SIZE = 10;
export const COLUMNS = 'ABCDEFGHIJ';

export type ShipType = 'battleship' | 'cruiser' | 'destroyer' | 'patrol';

export const SHIP_TYPES: readonly { type: ShipType; size: number; count: number }[] = [
  { type: 'battleship', size: 4, count: 1 },
  { type: 'cruiser', size: 3, count: 2 },
  { type: 'destroyer', size: 2, count: 3 },
  { type: 'patrol', size: 1, count: 4 },
];

/** Ship sizes of a complete fleet, largest first: 10 ships, 20 cells. */
export const FLEET_SIZES: readonly number[] = SHIP_TYPES.flatMap((s) => Array<number>(s.count).fill(s.size));

export function shipTypeForSize(size: number): ShipType | null {
  return SHIP_TYPES.find((s) => s.size === size)?.type ?? null;
}

export type Orientation = 'h' | 'v';

export interface Cell {
  x: number;
  y: number;
}

/**
 * A ship on the board. (x, y) is its first cell: the leftmost cell of a horizontal ship,
 * the bottom cell of a vertical ship.
 */
export interface ShipPlacement {
  size: number;
  x: number;
  y: number;
  orientation: Orientation;
}

export function cellLabel(cell: Cell): string {
  return `${COLUMNS[cell.x]}${cell.y + 1}`;
}

export function parseCellLabel(label: string): Cell | null {
  const m = /^([A-J])(10|[1-9])$/i.exec(label.trim());
  if (!m) return null;
  return { x: COLUMNS.indexOf(m[1]!.toUpperCase()), y: Number(m[2]) - 1 };
}

export function inBounds(cell: Cell): boolean {
  return (
    Number.isInteger(cell.x) && Number.isInteger(cell.y) && cell.x >= 0 && cell.y >= 0 && cell.x < BOARD_SIZE && cell.y < BOARD_SIZE
  );
}

export function sameCell(a: Cell, b: Cell): boolean {
  return a.x === b.x && a.y === b.y;
}

export function shipCells(ship: ShipPlacement): Cell[] {
  return Array.from({ length: ship.size }, (_, i) =>
    ship.orientation === 'h' ? { x: ship.x + i, y: ship.y } : { x: ship.x, y: ship.y + i },
  );
}

/** The 8 surrounding cells of a cell that are on the board. */
export function neighbours(cell: Cell): Cell[] {
  const result: Cell[] = [];
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      if (dx === 0 && dy === 0) continue;
      const n = { x: cell.x + dx, y: cell.y + dy };
      if (inBounds(n)) result.push(n);
    }
  }
  return result;
}
